import Highcharts, { SeriesOptionsType } from 'highcharts';
import _ from 'lodash';
import { HighchartsChartModule } from 'highcharts-angular';
import { Component, computed, effect, Input, OnDestroy, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, combineLatestWith, filter, map, merge, Observable, Subject, Subscription, switchMap, tap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { matImports, ngImports } from '@app/shared/imports';
import { StatsService } from '../stats.service';
import { StatsTransferService } from '../stats-transfer.service';
import { Dictionary } from '@app/core/models';
import {
  StatsIntervalsForm,
  StatsAction,
  StatsActionType,
  StatsIntervalsOptions,
  SeriesResult,
  StatsGraphType,
  StatsOptionsForm,
  StatsOptions,
  PieResult,
  FieldInfo,
  AdditionalFormData,
  AggFunction
} from '../models';
import { defaultPieConfig, defaultSeriesConfig, legendIndexes, seriesColors } from './stats-series.config';
import { viewValues } from '../stats.config';

@Component({
  selector: 'app-stats-series',
  templateUrl: './stats-series.component.html',
  styleUrls: ['./stats-series.component.scss'],
  standalone: true,
  imports: [...ngImports, ...matImports, TranslateModule, HighchartsChartModule],
  host: {
    class: 'mat-elevation-z3'
  }
})
export class StatsSeriesComponent implements OnInit, OnDestroy {

  @Input() values: Dictionary<string, string>;
  @Input() type: StatsGraphType = StatsGraphType.SERIES;

  private subscriptions = new Subscription();
  private actionSubject = new Subject<StatsAction>();
  private onInit = new Subject<void>();

  readonly Highcharts: typeof Highcharts = Highcharts; // required
  readonly graphType = StatsGraphType;

  chartOptions: WritableSignal<Highcharts.Options> = signal(null);
  empty: Signal<boolean> = computed(() => {
    if(this.type === StatsGraphType.SERIES) {
      return !this.chartOptions()?.series?.length;
    }
    const pieData = this.chartOptions()?.series?.[0] as any;
    return !pieData.data?.length;
  });

  name: Signal<string> = computed(() => {
    if (!this.chartOptions()) {
      return '';
    }

    const info = this.transfer.info();
    const options = this.form.toOptions();

    let value = '?';
    let group = '?';
    let interval = '';

    if (options?.value && info[options.value]) {
      value = this.translate.instant(info[options.value]?.viewValue);
    }
    if (options?.group && info[options.group]) {
      group = this.translate.instant(info[options.group]?.viewValue);
    }
    if (options?.startDate) {
      interval = `${this.translate.instant('anms.stats.interval.from')} ${options.startDate.toLocaleDateString()}`;
    }
    if (options?.endDate) {
      interval = `${interval} ${this.translate.instant('anms.stats.interval.to')} ${options.endDate.toLocaleDateString()}`;
    }

    const vars =  { value, group };

    const name = this.type === StatsGraphType.SERIES ? 'anms.stats.series.name' : 'anms.stats.pie.name';
    const nameTranslated = this.translate.instant(name, vars);

    return `${nameTranslated} ${interval}`;
  });

  settingsActice = computed(() => this.transfer.formData()?.type === this.type);

  queryParams$ = this.route.queryParamMap.pipe(
    combineLatestWith(this.onInit),
    tap(([params]) => this.onQueryParamsChange(params)),
    map(([params]) => params)
  );

  series$ = this.actionSubject.pipe(
    filter(action => action.type === StatsActionType.GET_SERIES),
    switchMap((action) => action.graphType === StatsGraphType.SERIES
      ? this.stats.getSeries(action.options as StatsIntervalsOptions)
      : this.stats.getPie(action.options)),
    tap((res) => this.setGraphResult(res)),
    catchError((error: unknown, observable: Observable<any>) => {
      return observable;
    })
  );

  formSuccess$ = this.transfer.sharedSubject.pipe(
    filter(action => action.type === StatsActionType.FORM_SUCCESS),
    filter(() => this.settingsActice()),
    tap(() => this.onSaveForm()),
  );

  formReset$ = this.transfer.sharedSubject.pipe(
    filter(action => action.type === StatsActionType.FORM_RESET),
    filter(() => this.settingsActice()),
    tap(() => this.onResetForm()),
  );

  observables$ = merge(this.series$, this.formSuccess$, this.formReset$, this.queryParams$);

  form: StatsOptionsForm;
  lastValue: StatsOptions = null;
  formData: AdditionalFormData;

  enumFields: WritableSignal<string[]> = signal([]);


  constructor(
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private stats: StatsService,
    public transfer: StatsTransferService,
  ) {
    effect(() => {
      if (this.transfer.ready()) {
        this.fillForm(this.transfer.info());
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.subscriptions.add(this.observables$.subscribe());

    if (this.type === StatsGraphType.SERIES) {
      this.form = new StatsIntervalsForm();
    } else {
      this.form = new StatsOptionsForm();
    }

    this.onInit.next();
  }

  onQueryParamsChange(paramMap: ParamMap): void {
    const queryParams: Dictionary<string, string> = paramMap.keys.reduce((acc, key) => ({ ...acc, [key]: paramMap.get(key) }), {});

    const value = this.stats.fromQueryParams(queryParams, this.type);

    if (!value && this.transfer.ready()) {
      this.fillForm(this.transfer.info());
    }

    if (!value || _.isEqual(this.lastValue, value)) {
      return;
    }

    this.form.deserialize(value);
    this.lastValue = value;

    this.actionSubject.next({ type: StatsActionType.GET_SERIES, options: this.lastValue, graphType: this.type });
    this.onResetForm();
  }

  onClickSettings(): void {
    if (this.settingsActice()) {
      this.transfer.formData.set(null);
      return;
    }
    this.transfer.form = this.form;
    this.transfer.formData.set(this.formData);
  }

  setGraphResult(res: SeriesResult | PieResult): void {
    if (res instanceof SeriesResult) {
      this.setSeriesData(res);
    }

    if (res instanceof PieResult) {
      this.setPieData(res);
    }
  }

  setSeriesData(seriesResult: SeriesResult): void {
    for (const item of seriesResult.series) {
      if (seriesColors[item.name]) {
        item.color = seriesColors[item.name];
      }

      if (legendIndexes[item.name]) {
        item.legendIndex = legendIndexes[item.name];
      }

      if (viewValues[item.name]) {
        item.name = this.translate.instant(viewValues[item.name]);
      }
    }

    const series: SeriesOptionsType[] = seriesResult.series.map(line => ({ ...line, type: 'line' }));

    const chartConfig = _.cloneDeep(defaultSeriesConfig);
    chartConfig.xAxis = { ...chartConfig.xAxis, categories: seriesResult.categories };

    if (seriesResult.name === AggFunction.SUM) {
      chartConfig.tooltip = { formatter: this.stats.getSizeFormatterFn() };
    } else {
      chartConfig.tooltip = { formatter: undefined };
    }

    this.chartOptions.set({
      ...chartConfig,
      series,
    });
  }

  setPieData(pieResult: PieResult): void {
    const result = pieResult.serialize();

    if (result.data) {
      for (const item of result.data) {
        if (viewValues[item.name]) {
          item.name = this.translate.instant(viewValues[item.name]);
        }
      }
    }

    const series = { type: 'pie', ...result };

    const config = _.cloneDeep(defaultPieConfig);
    config.series = [series];

    if (pieResult.name === AggFunction.SUM) {
      config.tooltip = { formatter: this.stats.getSizeFormatterFn() };
    } else {
      config.tooltip = { formatter: undefined };
    }

    this.chartOptions.set(config);
  }

  fillForm(info: Dictionary<string, FieldInfo>): void {
    const controlNames: string[] = [];
    const groupOptions: FieldInfo[] = [];
    const valueOptions: FieldInfo[] = [];

    const value = this.form.value;
    if (!value.filter) {
      value.filter = { };
    }

    Object.keys(info).forEach(field => {
      const fieldInfo = info[field];

      if (!fieldInfo) {
        return;
      }

      if (fieldInfo.group) {
        if (!this.form.group.value || fieldInfo.groupDefault) {
          value.group = field;
        }
        groupOptions.push(fieldInfo);
      }

      if (fieldInfo.aggFunction?.length) {
        if (!value.value) {
          value.value = fieldInfo.field;
          value.aggFunction = fieldInfo.aggFunction[0];
        }

        valueOptions.push(fieldInfo);
      }

      switch (fieldInfo.type) {
        case 'checkbox':
          this.form.filter.addControl(field, new FormControl(null));
          value.filter[field] = null;
          controlNames.push(field);
          break;
        case 'select':
          this.form.filter.addControl(field, new FormControl(fieldInfo.values || []));
          value.filter[field] = fieldInfo.values || [];
          controlNames.push(field);
          break;
        default:
          break;
      }
    });

    this.formData = { type: this.type, controlNames, groupOptions, valueOptions };
    this.form.setValue(value);
    this.onSaveForm();
  }


  onSaveForm(): void {
    const params = this.stats.toQueryParams(this.form.toOptions(), this.type);

    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: { ...params },
        queryParamsHandling: 'merge'
      }
    );
  }

  onResetForm(): void {
    this.form.deserialize(this.lastValue);
    this.transfer.formData.set(null);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
